import { environmentHttpClient } from "client/Api/httpClient";
import { TagViewModel } from "client/Api/Tags/tags";
import {
	UserProfileCustomStringViewModel,
	UserPronounKind,
	WorkingDaysModel
} from "../UserProfile/service";

export enum ContactSource {
	Personal = 0,
	Suggested = 1,
	Gal = 2,
	Workspace = 3
}

export enum ContactKind {
	Exchange = 1,
	Workspace = 2
}

export enum WorkspaceContactSortBy {
	Default = 0,
	FirstName = 1,
	LastName = 2,
	JobTitle = 3,
	Department = 4,
	OfficeLocation = 5
}

export enum ExchangeContactSortBy {
	Default = 0,
	FirstName = 1,
	LastName = 2,
	JobTitle = 3,
	Department = 4,
	Company = 5,
	OfficeLocation = 6
}

export type ExchangeContactDetailsModel = {
	kind: ContactKind.Exchange;

	id: string;
	firstName: string | null;
	middleName: string | null;
	lastName: string | null;
	displayName: string | null;

	photoUrl: string | null;

	emailAddress: string | null;
	emailAddress2: string | null;
	emailAddress3: string | null;

	businessPhone: string | null;
	businessPhone2: string | null;
	homePhone: string | null;
	homePhone2: string | null;
	mobilePhone: string | null;
	mobilePhone2: string | null;

	jobTitle: string | null;
	department: string | null;
	officeLocation: string | null;
	company: string | null;

	homeAddress: ContactAddress | null;
	businessAddress: ContactAddress | null;
	otherAddress: ContactAddress | null;

	birthday: string | null;

	notes: string | null;
};

export type WorkspaceContactDetailsModel = {
	kind: ContactKind.Workspace;

	id: string;
	firstName: string | null;
	lastName: string | null;
	emailAddress: string | null;
	photoUrl: string | null;
	backgroundImageUrl: string | null;
	phone: string | null;
	jobTitle: string | null;
	department: string | null;
	officeLocation: string | null;
	birthday: string | null;
	presenceStatus: ContactPresenceStatus;
	aboutMe: string | null;
	workingDays: WorkingDaysModel | null;
	projects: readonly TagViewModel[];
	education: readonly TagViewModel[];
	skills: readonly TagViewModel[];
	hobbies: readonly TagViewModel[];
	pronouns: UserPronounKind;
	customString1: OptionalModel<UserProfileCustomStringViewModel>;
	customString2: OptionalModel<UserProfileCustomStringViewModel>;
	customString3: OptionalModel<UserProfileCustomStringViewModel>;
	customString4: OptionalModel<UserProfileCustomStringViewModel>;
	customString5: OptionalModel<UserProfileCustomStringViewModel>;
};

export type ContactDetailsModel =
	| ExchangeContactDetailsModel
	| WorkspaceContactDetailsModel;

export const enum ContactPresenceStatus {
	NotApplicable = 0,
	Available = 1,
	Busy = 2,
	DoNotDisturb = 3,
	Away = 4,
	BeRightBack = 5,
	Offline = 6,
	OutOfOffice = 7,
	Unknown = 8
};

export type ContactListItem = {
	id: string;
	firstName: string | null;
	middleName: string | null;
	lastName: string | null;
	displayName: string | null;
	jobTitle: string | null;
	phoneNumber: string | null;
	emailAddress: string | null;
	company: string | null;
	department: string | null;
	officeLocation: string | null;
	photoUrl: string | null;
	presenceStatus: ContactPresenceStatus;
	skills: readonly TagViewModel[] | null;
};

export type ContactAddress = {
	street: string | null;
	city: string | null;
	postcode: string | null;
	state: string | null;
	country: string | null;
};

export type SaveContactModel = {
	firstName: string;
	middleName: string;
	lastName: string;

	emailAddress: string;
	emailAddress2: string;
	emailAddress3: string;

	businessPhone: string;
	businessPhone2: string;
	homePhone: string;
	homePhone2: string;
	mobilePhone: string;
	mobilePhone2: string;

	jobTitle: string;
	department: string;
	company: string;

	homeAddress: ContactAddress;
	businessAddress: ContactAddress;
	otherAddress: ContactAddress;

	birthday: Date | null;

	notes: string;
};

export interface DisplayContactModel {
	id: string;
	displayName: string;
	emailAddress: string;
}

export function listPersonalContacts(
	searchTerm: string,
	pageSize: number,
	skip: number,
	sortBy: ExchangeContactSortBy,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<ContactListItem>> {
	return environmentHttpClient.get<LazyLoadPageResult<ContactListItem>>(
		"/Email/Contacts/ListPersonalContactsForSidebar",
		{ searchTerm, pageSize, skip, sortBy },
		{ signal }
	);
}

export function listGlobalContacts(
	searchTerm: string,
	pageSize: number,
	skipToken: string | null,
	signal?: AbortSignal
): Promise<SkipTokenPageResult<ContactListItem>> {
	return environmentHttpClient.get<SkipTokenPageResult<ContactListItem>>(
		"/Email/Contacts/ListGlobalContactsForSidebar",
		{ searchTerm, pageSize, skipToken },
		{ signal }
	);
}

export function listWorkspaceContacts(
	searchTerm: string,
	officeLocation: string | null,
	department: string | null,
	pageSize: number,
	skip: number,
	sortBy: WorkspaceContactSortBy,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<ContactListItem>> {
	return environmentHttpClient.get<LazyLoadPageResult<ContactListItem>>(
		"/Email/Contacts/ListWorkspaceContactsForSidebar",
		{
			searchTerm,
			officeLocation,
			department,
			pageSize,
			skip,
			sortBy
		},
		{ signal }
	);
}

export function getPersonalContactDetails(
	id: string,
	signal?: AbortSignal
): Promise<ExchangeContactDetailsModel> {
	return environmentHttpClient.get<ExchangeContactDetailsModel>(
		"/Email/Contacts/GetPersonalContactDetails",
		{ id },
		{ signal }
	);
}

export function getGlobalContactDetails(
	id: string,
	signal: AbortSignal
): Promise<ExchangeContactDetailsModel> {
	return environmentHttpClient.get<ExchangeContactDetailsModel>(
		"/Email/Contacts/GetGlobalContactDetails",
		{ id },
		{ signal }
	);
}

export function getWorkspaceContactDetails(
	id: string,
	signal: AbortSignal
): Promise<WorkspaceContactDetailsModel> {
	return environmentHttpClient.get<WorkspaceContactDetailsModel>(
		"/Email/Contacts/GetWorkspaceContactDetails",
		{ id },
		{ signal }
	);
}

export function createPersonalContact(
	model: SaveContactModel,
	signal?: AbortSignal
): Promise<string> {
	return environmentHttpClient.post<string>(
		"/Email/Contacts/CreatePersonalContact",
		model,
		{ signal }
	);
}

export function updatePersonalContact(
	id: string,
	model: SaveContactModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		`/Email/Contacts/UpdatePersonalContact?id=${encodeURIComponent(id)}`,
		model,
		{ signal }
	);
}

export function deletePersonalContact(
	id: string,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.delete<void>(
		`/Email/Contacts/DeletePersonalContact?id=${encodeURIComponent(id)}`,
		undefined,
		{ signal }
	);
}

export function getContactsStartsWith(
	startsWith: string,
	maxNumberOfItems: number | null = null,
	signal?: AbortSignal
): Promise<readonly DisplayContactModel[]> {
	return environmentHttpClient
		.get<readonly DisplayContactModel[]>(
			"Email/Contacts/GetContactsStartsWith",
			{
				startsWith,
				maxNumberOfItems
			},
			{ signal }
		);
}

export type ContactsSearchResult = {
	contacts: DisplayContactModel[];
	total: number;
};

export function findPersonalContacts(
	searchInput: string,
	pageSize: number,
	skip: number,
	signal?: AbortSignal
): Promise<ContactsSearchResult> {
	return environmentHttpClient.get<ContactsSearchResult>(
		"Email/Contacts/FindPersonalContacts",
		{
			searchInput,
			pageSize,
			skip
		},
		{ signal }
	);
}

export type WorkspaceContactsPresence = {
	[key: string]: ContactPresenceStatus;
};

export function getWorkspaceContactsPresence(
	contactIds: readonly string[],
	signal: AbortSignal
): Promise<WorkspaceContactsPresence> {
	return environmentHttpClient.post<WorkspaceContactsPresence>(
		"Email/Contacts/Presence",
		contactIds,
		{ signal }
	);
}

export function getWorkspaceContactIdFromUserId(userId: number): string {
	return userId.toString();
}

export function listWorkspaceContactsOfficeLocations(
	signal?: AbortSignal
): Promise<readonly string[]>{
	return environmentHttpClient.get<readonly string[]>(
		"Email/Contacts/ListWorkspaceContactsOfficeLocations",
		undefined,
		{ signal }
	);
}

export function listWorkspaceContactsDepartments(
	signal?: AbortSignal
): Promise<readonly string[]>{
	return environmentHttpClient.get<readonly string[]>(
		"Email/Contacts/ListWorkspaceContactsDepartments",
		undefined,
		{ signal }
	);
}

export function listWorkspaceContactsJobTitles(
	signal?: AbortSignal
): Promise<readonly string[]>{
	return environmentHttpClient.get<readonly string[]>(
		"Email/Contacts/ListWorkspaceContactsJobTitles",
		undefined,
		{ signal }
	);
}
